import { useQuery } from "@tanstack/react-query";
import { apiGetFieldPermissions, type FieldPermission } from "@/lib/api";
import type { RoleConfig } from "@/contexts/AuthContext";

// Check if a superadmin impersonation session is currently active.
// Evaluated once at module level per render cycle — avoids repeated
// JSON.parse inside hot canEdit() calls.
function isImpersonating(): boolean {
  try {
    const raw = localStorage.getItem("besthr_sa_impersonation");
    if (!raw) return false;
    const imp = JSON.parse(raw);
    return !!(imp.token && new Date(imp.expiresAt) > new Date());
  } catch {
    return false;
  }
}

export function useFieldPermissions() {
  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ["field-permissions"],
    queryFn: apiGetFieldPermissions,
    staleTime: 5 * 60 * 1000,
  });

  const permissionMap = new Map<string, FieldPermission>();
  for (const p of permissions) {
    permissionMap.set(p.fieldName, p);
  }

  /**
   * @param isLineManagerOfEmployee - true if the viewer is the direct line manager of the employee being viewed
   */
  function canEdit(
    fieldName: string,
    viewerRole: RoleConfig,
    isOwnProfile: boolean,
    isLineManagerOfEmployee: boolean = false
  ): boolean {
    // Superadmin impersonation = read-only, no edits allowed
    if (isImpersonating()) return false;

    const perm = permissionMap.get(fieldName);
    if (!perm) return false;

    const { mainRole } = viewerRole;

    if (mainRole === "admin") return perm.editableByAdmin;
    if (mainRole === "hr") return perm.editableByHr;

    if (mainRole === "user") {
      if (isOwnProfile) return perm.editableByUser;
      if (isLineManagerOfEmployee) {
        return perm.editableByLineManager;
      }
    }

    return false;
  }

  function canEditAnyInSection(
    section: string,
    viewerRole: RoleConfig,
    isOwnProfile: boolean,
    isLineManagerOfEmployee: boolean = false
  ): boolean {
    // Superadmin impersonation = read-only, no edits allowed
    if (isImpersonating()) return false;

    const sectionPerms = permissions.filter((p) => p.section === section);
    return sectionPerms.some((p) => canEdit(p.fieldName, viewerRole, isOwnProfile, isLineManagerOfEmployee));
  }

  function getPermissionsForSection(section: string): FieldPermission[] {
    return permissions.filter((p) => p.section === section);
  }

  return {
    permissions,
    isLoading,
    canEdit,
    canEditAnyInSection,
    getPermissionsForSection,
  };
}
